import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader,
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, SyutSearchGrid, MinimizedModalStrip } from 'components/ui';
import TenantSelector from 'components/common/TenantSelector';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useTenantUsers, useDeleteTenantUser, useResetTenantUserPassword, useTenantSummaries } from 'hooks/api';
import type { TenantUserResponse, TenantHubPagedRequest, SetPasswordRequest } from 'api/types';
import TenantUserFormModal from '../../../modals/modules/user-management/TenantUserFormModal';
import TenantUserViewModal from '../../../modals/modules/user-management/TenantUserViewModal';
import TenantUserRolesModal from '../../../modals/modules/user-management/TenantUserRolesModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid' | 'calendar';
type TenantUserGridRow = TenantUserResponse & { id: string };

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'userName', label: 'Username', field: 'userName', required: false, defaultVisible: true, sortable: true },
  { id: 'firstName', label: 'First Name', field: 'firstName', required: false, defaultVisible: true, sortable: true },
  { id: 'lastName', label: 'Last Name', field: 'lastName', required: false, defaultVisible: true, sortable: true },
  { id: 'email', label: 'Email', field: 'email', required: false, defaultVisible: true, sortable: true },
  { id: 'privilegeLevel', label: 'Privilege', field: 'privilegeLevel', required: false, defaultVisible: true, sortable: true },
  { id: 'status', label: 'Status', field: 'statusCode', required: false, defaultVisible: true, sortable: true },
  { id: 'isPrimaryTenant', label: 'Primary', field: 'isPrimaryTenant', required: false, defaultVisible: true, sortable: true },
  { id: 'lastActivityAt', label: 'Last Activity', field: 'lastActivityAt', required: false, defaultVisible: false, sortable: true },
  { id: 'createdAt', label: 'Created', field: 'createdAt', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '2px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              borderRadius: '0.75rem 0.75rem 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
              {columns.map(column => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.625rem 1.25rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    gap: '0.75rem',
                    opacity: column.required ? 0.6 : 1,
                  }}
                >
                  <div style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--border-color)',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    ...(selectedColumns.includes(column.id) ? {
                      background: 'var(--accent-gradient)',
                      borderColor: 'var(--primary)',
                      color: 'white',
                    } : {}),
                  }}>
                    {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                    {column.label}{column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              padding: '1rem',
              borderTop: '2px solid var(--border-color)',
              background: 'var(--bg-primary)',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  Reset Default
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                <button onClick={handleApply} className="btn btn-primary">Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// HELPERS
// ============================================================================

const getPrivilegeBadge = (level: string) => {
  switch (level?.toLowerCase()) {
    case 'super_admin': return <span className="badge badge-danger">Super Admin</span>;
    case 'admin': return <span className="badge badge-warning">Admin</span>;
    case 'support': return <span className="badge badge-primary">Support</span>;
    case 'user': return <span className="badge badge-info">User</span>;
    default: return <span className="badge badge-default">{level || '-'}</span>;
  }
};

const getStatusDisplay = (statusCode?: string) => {
  const code = (statusCode || '').toLowerCase();
  if (code.includes('active')) return { label: 'Active', className: 'badge-active' };
  if (code.includes('inactive') || code.includes('disabled')) return { label: 'Inactive', className: 'badge-inactive' };
  if (code.includes('suspended')) return { label: 'Suspended', className: 'badge-suspended' };
  return { label: statusCode || '-', className: 'badge-default' };
};

// ============================================================================
// USER CARD COMPONENT (Grid View)
// ============================================================================

interface UserCardProps {
  user: TenantUserGridRow;
  onView: (user: TenantUserGridRow) => void;
  onEdit: (user: TenantUserGridRow) => void;
  onResetPassword: (user: TenantUserGridRow) => void;
  onDelete: (user: TenantUserGridRow) => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, onView, onEdit, onResetPassword, onDelete }) => {
  const menuItems: ActionMenuItem[] = useMemo(() => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(user) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(user) },
    { id: 'reset-password', label: 'Reset Password', icon: <IconSystem.lock size={18} />, onClick: () => onResetPassword(user) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(user), variant: 'danger' },
  ], [user, onView, onEdit, onResetPassword, onDelete]);

  const statusDisplay = getStatusDisplay(user.statusCode);

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {user.userName}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.25rem' }}>
            {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
          </h3>
          {user.email && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{user.email}</div>
          )}
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Privilege</div>
          {getPrivilegeBadge(user.privilegeLevel)}
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge ${statusDisplay.className}`}>{statusDisplay.label}</span>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Primary Tenant</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{user.isPrimaryTenant ? 'Yes' : 'No'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Last Activity</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {user.lastActivityAt ? new Date(user.lastActivityAt).toLocaleDateString() : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface TenantUserMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const TenantUserMasterScreen: React.FC<TenantUserMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, getMinimizedModal, dismiss } = useModalMinimize();
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('');

  // ---- Tenant selection ----
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const { data: tenantSummaries } = useTenantSummaries();
  const selectedTenantName = useMemo(() => {
    if (!selectedTenantId || !tenantSummaries.length) return '';
    const t = tenantSummaries.find(s => s.tenantId === selectedTenantId);
    return t ? `${t.tenantCode} — ${t.tenantName}` : '';
  }, [selectedTenantId, tenantSummaries]);
  const handleTenantChange = useCallback((tenantId: string) => {
    setSelectedTenantId(tenantId);
    setQueryParams(prev => ({ ...prev, pageNumber: 1, filters: undefined, searchTerm: undefined }));
    setSearchQuery('');
  }, []);

  // ---- API state (server-side pagination) ----
  const [queryParams, setQueryParams] = useState<TenantHubPagedRequest>({
    pageNumber: 1,
    pageSize: 25,
  });
  const { data: users, pagination: paginationMeta, loading, refetch } = useTenantUsers(selectedTenantId, queryParams);
  const { deleteTenantUser, loading: deleting } = useDeleteTenantUser();
  const { resetPassword, loading: resettingPassword } = useResetTenantUserPassword();

  // Map users to include `id` field required by SyutGrid
  const gridData: TenantUserGridRow[] = useMemo(
    () => users.map(u => ({ ...u, id: u.id })),
    [users],
  );

  // ---- Server-side pagination handlers ----
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, pageNumber: pageIndex + 1 })); // API uses 1-based
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, pageNumber: 1 })); // reset to first page
  }, []);

  // ---- Column visibility ----
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // ---- UI state ----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [privilegeFilter, setPrivilegeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [, setSelectedRows] = useState<TenantUserGridRow[]>([]);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);

  // ---- Modal state ----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUserResponse | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Off by default: a temporary password leaves a required action pending at the identity
  // provider, and an account in that state cannot sign in through the Hub (ACCOUNT_NOT_READY)
  // until the password is changed at the provider — Centrix offers no such step.
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);

  // ---- Load saved column preferences ----
  useEffect(() => {
    const saved = localStorage.getItem('tenant-user-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // ---- Search debounce ----
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        pageNumber: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ---- Apply filter panel values to query params (server-side) ----
  const applyFilters = useCallback(() => {
    const filters: Record<string, any> = {};
    if (usernameFilter.trim()) filters.userName = usernameFilter.trim();
    if (nameFilter.trim()) filters.name = nameFilter.trim();
    if (emailFilter.trim()) filters.email = emailFilter.trim();
    if (privilegeFilter) filters.privilegeLevel = privilegeFilter;
    if (statusFilter) filters.statusCode = statusFilter;
    setQueryParams(prev => ({
      ...prev,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      pageNumber: 1,
    }));
    setFilterOpen(false);
  }, [usernameFilter, nameFilter, emailFilter, privilegeFilter, statusFilter]);

  // ---- F2 keyboard shortcut for search popup ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen && !resetPasswordModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen, resetPasswordModalOpen]);

  // ---- Column visibility handlers ----
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('tenant-user-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('tenant-user-master-columns');
  }, []);

  // ---- Handlers ----
  const handleView = useCallback((user: TenantUserResponse) => {
    const viewId = tabId ? `${tabId}-view-modal-${user.userName}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismiss(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedUser(user);
    setViewModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleRestoreView = useCallback((userName: string) => {
    const user = users.find(u => u.userName === userName);
    if (user) {
      setSelectedUser(user);
      setViewModalOpen(true);
    }
  }, [users]);

  const handleEdit = useCallback((user: TenantUserResponse) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${user.userName}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismiss(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedUser(user);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedUser(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user: TenantUserResponse) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }, []);

  /** Opens the role assignment modal for this user, within the selected tenant. */
  const handleManageRoles = useCallback((user: TenantUserResponse) => {
    setSelectedUser(user);
    setRolesModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedUser || !selectedTenantId) return;
    try {
      await deleteTenantUser(selectedTenantId, selectedUser.userId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
          notificationService.toast.success(`User "${selectedUser.userName}" has been deleted.`);
          refetch();
        },
      });
    } catch {
      notificationService.toast.error('Failed to delete user. Please try again.');
    }
  }, [selectedUser, selectedTenantId, deleteTenantUser, refetch]);

  const handleResetPasswordClick = useCallback((user: TenantUserResponse) => {
    setSelectedUser(user);
    setNewPassword('');
    setNewPasswordError('');
    setShowNewPassword(false);
    setRequirePasswordChange(false);
    setResetPasswordModalOpen(true);
  }, []);

  const handleConfirmResetPassword = useCallback(async () => {
    if (!selectedUser || !selectedTenantId) return;
    if (!newPassword.trim()) {
      setNewPasswordError('Password is required');
      return;
    }
    // Mirror backend ValidationService.ValidatePassword rules
    if (newPassword.length < 8) {
      setNewPasswordError('Minimum 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setNewPasswordError('Must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setNewPasswordError('Must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setNewPasswordError('Must contain at least one number');
      return;
    }
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(newPassword)) {
      setNewPasswordError('Must contain at least one special character (!@#$%...)');
      return;
    }
    const data: SetPasswordRequest = { password: newPassword, temporary: requirePasswordChange };
    try {
      await resetPassword(selectedTenantId, selectedUser.userId, data, {
        onSuccess: () => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
          setNewPassword('');
          notificationService.toast.success(
            requirePasswordChange
              ? `Password reset for "${selectedUser.userName}". They must change it at their next sign-in.`
              : `Password reset for "${selectedUser.userName}".`,
          );
        },
      });
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message;
      notificationService.toast.error(apiMessage || err?.message || 'Failed to reset password. Please try again.');
    }
  }, [selectedUser, selectedTenantId, newPassword, requirePasswordChange, resetPassword]);

  const handleFormSaveSuccess = useCallback((savedTenantId?: string) => {
    setFormModalOpen(false);
    setSelectedUser(null);
    if (savedTenantId && savedTenantId !== selectedTenantId) {
      // User created under a different tenant — switch to it (triggers refetch via useTenantUsers)
      setSelectedTenantId(savedTenantId);
    } else {
      refetch();
    }
  }, [refetch, selectedTenantId]);

  // ---- Grid columns ----
  const tableColumns: SyutGridColumn<TenantUserGridRow>[] = useMemo(() => [
    { id: 'userName', header: 'Username', accessorKey: 'userName', size: 150 },
    { id: 'firstName', header: 'First Name', accessorKey: 'firstName', size: 140 },
    { id: 'lastName', header: 'Last Name', accessorKey: 'lastName', size: 140 },
    {
      id: 'email', header: 'Email', accessorKey: 'email',
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'privilegeLevel',
      header: 'Privilege',
      accessorKey: 'privilegeLevel',
      size: 120,
      cell: ({ getValue }) => getPrivilegeBadge(getValue() as string),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      cell: ({ getValue }) => {
        const status = getStatusDisplay(getValue() as string);
        return <span className={`badge ${status.className}`}>{status.label}</span>;
      },
    },
    {
      id: 'isPrimaryTenant',
      header: 'Primary',
      accessorKey: 'isPrimaryTenant',
      size: 100,
      cell: ({ getValue }) => {
        const val = getValue() as boolean;
        return <span className={`badge ${val ? 'badge-active' : 'badge-default'}`}>{val ? 'Yes' : 'No'}</span>;
      },
    },
    {
      id: 'lastActivityAt',
      header: 'Last Activity',
      accessorKey: 'lastActivityAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
  ], []);

  // ---- Row actions ----
  const getRowActions = useCallback((user: TenantUserGridRow): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(user) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(user) },
    { id: 'manage-roles', label: 'Manage Roles', icon: <IconSystem.settings size={18} />, onClick: () => handleManageRoles(user) },
    { id: 'reset-password', label: 'Reset Password', icon: <IconSystem.lock size={18} />, onClick: () => handleResetPasswordClick(user) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(user), variant: 'danger' },
  ], [handleView, handleEdit, handleManageRoles, handleResetPasswordClick, handleDeleteClick]);

  // ---- View mode options ----
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // ---- Action buttons ----
  const actionButtons: ActionButton[] = [
    { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Users', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Users (F2)', variant: 'icon', visible: true },
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add User', onClick: handleAddNew, tooltip: 'Add New User', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => notificationService.toast.info('Export feature coming soon.'), tooltip: 'Export', variant: 'icon' },
  ];

  // ---- Clear filters ----
  const clearFilters = useCallback(() => {
    setUsernameFilter('');
    setNameFilter('');
    setEmailFilter('');
    setPrivilegeFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({ ...prev, filters: undefined, pageNumber: 1 }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Tenant Users"
        description="Manage users within tenants"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search users...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '3px' }}>
        <div className="data-grid-container">
          {/* Tenant Selector Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '4px 1rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '6px',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Tenant:</span>
            <div className="tenant-ribbon-selector" style={{ flex: '1 1 400px', maxWidth: '500px', minWidth: '200px' }}>
              <style dangerouslySetInnerHTML={{ __html: `
                .tenant-ribbon-selector .syut-select__control {
                  outline: 1px solid rgba(148, 163, 184, 0.35) !important;
                  outline-offset: -1px !important;
                  border: none !important;
                  box-shadow: none !important;
                  background: var(--bg-primary, #ffffff) !important;
                }
              `}} />
              <TenantSelector
                value={selectedTenantId}
                onChange={handleTenantChange}
                label=""
                placeholder="Select a tenant to manage users..."
                size="sm"
              />
            </div>
            {selectedTenantId && paginationMeta.totalCount > 0 && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {paginationMeta.totalCount} user{paginationMeta.totalCount !== 1 ? 's' : ''} total
              </span>
            )}
          </div>

          {!selectedTenantId ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.users size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Select a Tenant</h3>
              <p>Choose a tenant from the dropdown above to view and manage its users.</p>
            </div>
          ) : viewMode === 'list' ? (
            <SyutGrid<TenantUserGridRow>
              data={gridData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={paginationMeta.totalCount}
              currentPage={(queryParams.pageNumber ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              defaultPageSize={25}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage={loading ? 'Loading users...' : 'No users found'}
              isLoading={loading}
              visibleColumns={visibleColumns}
            />
          ) : viewMode === 'grid' ? (
            <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '1rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1.5rem',
                flex: 1,
                overflow: 'auto',
                alignContent: 'start',
              }}>
                {gridData.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    <IconSystem.users size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                    <p>{loading ? 'Loading users...' : 'No users found'}</p>
                  </div>
                ) : (
                  gridData.map(user => (
                    <UserCard
                      key={user.id}
                      user={user}
                      onView={handleView}
                      onEdit={handleEdit}
                      onResetPassword={handleResetPasswordClick}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </div>
              {gridData.length > 0 && (
                <div className="pagination-footer">
                  <div className="pagination-info">
                    Showing {gridData.length} of {paginationMeta.totalCount} users
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <select
                      value={queryParams.pageSize ?? 25}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>{size} / page</option>
                      ))}
                    </select>
                    <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.pageNumber ?? 1) <= 1}>
                      <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                    </button>
                    <button className="page-btn" onClick={() => handlePageChange((queryParams.pageNumber ?? 1) - 2)} disabled={(queryParams.pageNumber ?? 1) <= 1}>
                      <IconSystem.chevronLeft size={16} />
                    </button>
                    <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                      Page {queryParams.pageNumber ?? 1} of {Math.max(1, Math.ceil(paginationMeta.totalCount / (queryParams.pageSize ?? 25)))}
                    </span>
                    <button className="page-btn" onClick={() => handlePageChange(queryParams.pageNumber ?? 1)} disabled={(queryParams.pageNumber ?? 1) >= Math.ceil(paginationMeta.totalCount / (queryParams.pageSize ?? 25))}>
                      <IconSystem.chevronRight size={16} />
                    </button>
                    <button className="page-btn" onClick={() => handlePageChange(Math.ceil(paginationMeta.totalCount / (queryParams.pageSize ?? 25)) - 1)} disabled={(queryParams.pageNumber ?? 1) >= Math.ceil(paginationMeta.totalCount / (queryParams.pageSize ?? 25))}>
                      <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Tenant Users.</p>
            </div>
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                  borderRadius: '0.25rem',
                }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={usernameFilter}
                  onChange={(e) => setUsernameFilter(e.target.value)}
                  placeholder="Filter by username..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by first or last name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Email</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="Filter by email..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Privilege Level"
                  options={[
                    { value: '', label: 'All Levels' },
                    { value: 'user', label: 'User' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'support', label: 'Support' },
                    { value: 'super_admin', label: 'Super Admin' },
                  ]}
                  value={privilegeFilter}
                  onChange={(val: string | string[] | null) => setPrivilegeFilter((val as string) || '')}
                  placeholder="Select level..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Select status..."
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <TenantUserFormModal
        isOpen={formModalOpen}
        mode={formMode}
        user={selectedUser}
        tenantId={selectedTenantId}
        tenantName={selectedTenantName}
        onClose={() => { setFormModalOpen(false); setSelectedUser(null); }}
        onMinimize={() => setFormModalOpen(false)}
        onRestoreModal={(restoredMode, instanceId?, userName?) => {
          if (instanceId) setCreateInstanceId(instanceId);
          if (userName) {
            const u = users.find(r => r.userName === userName);
            if (u) setSelectedUser(u);
          }
          setFormMode(restoredMode);
          setFormModalOpen(true);
        }}
        onSaveSuccess={handleFormSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
      />

      {/* View Modal */}
      <TenantUserViewModal
        isOpen={viewModalOpen}
        user={selectedUser}
        tenantName={selectedTenantName}
        onClose={() => { setViewModalOpen(false); setSelectedUser(null); }}
        onEdit={(user) => { setViewModalOpen(false); handleEdit(user); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Role assignment — scoped to the tenant selected above */}
      <TenantUserRolesModal
        isOpen={rolesModalOpen}
        user={selectedUser}
        tenantId={selectedTenantId}
        tenantLabel={selectedTenantName}
        onClose={() => { setRolesModalOpen(false); setSelectedUser(null); }}
        onSaved={refetch}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={(userName) => {
          const u = users.find(x => x.userName === userName);
          if (u) setSelectedUser(u);
          setRolesModalOpen(true);
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete User"
        message={`Are you sure you want to delete user "${selectedUser?.userName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedUser(null); }}
      />

      {/* Reset Password Modal */}
      {resetPasswordModalOpen && (
        <>
          <div className="modal-backdrop" onClick={() => { setResetPasswordModalOpen(false); setSelectedUser(null); setNewPassword(''); setNewPasswordError(''); }} />
          <div className="modal-container modal-md">
            {resettingPassword && (
              <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
                <div style={{ width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Resetting password...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.lock size={20} />
                Reset Password
              </h2>
              <button className="modal-close" onClick={() => { setResetPasswordModalOpen(false); setSelectedUser(null); setNewPassword(''); setNewPasswordError(''); }}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                Set a new password for user &quot;{selectedUser?.userName}&quot; and hand it over to them.
              </p>
              <div className="form-group">
                <label className="form-label">New Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className={`form-input ${newPasswordError ? 'form-field-error' : ''}`}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(''); }}
                    placeholder="Min 8 characters"
                    autoComplete="new-password"
                    style={{ paddingRight: '2.25rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(prev => !prev)}
                    title={showNewPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem',
                      color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
                    }}
                  >
                    {showNewPassword ? <IconSystem.eyeOff size={16} /> : <IconSystem.eye size={16} />}
                  </button>
                </div>
                {newPasswordError && <div className="form-error-message">{newPasswordError}</div>}
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={requirePasswordChange}
                    onChange={(e) => setRequirePasswordChange(e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    Require a password change at the next sign-in
                    <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      Only for applications that sign in on the identity provider&apos;s own page. An account
                      with a pending change cannot sign in through the Hub (Centrix) until it is done.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setResetPasswordModalOpen(false); setSelectedUser(null); setNewPassword(''); setNewPasswordError(''); }} disabled={resettingPassword}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmResetPassword} disabled={resettingPassword}>
                Reset Password
              </button>
            </div>
          </div>
        </>
      )}

      {/* User Search Popup (F2) */}
      {selectedTenantId && (
        <SyutSearchGrid<TenantUserGridRow>
          isOpen={searchPopupOpen}
          onClose={() => setSearchPopupOpen(false)}
          onSelect={(user) => {
            handleView(user);
          }}
          title="Search Users"
          columns={[
            { key: 'userName', header: 'Username', width: '150px', sortable: true },
            { key: 'firstName', header: 'First Name', width: '140px', sortable: true },
            { key: 'lastName', header: 'Last Name', width: '140px', sortable: true },
            { key: 'email', header: 'Email', width: '200px', sortable: true },
            {
              key: 'privilegeLevel',
              header: 'Privilege',
              width: '120px',
              render: (value: any) => getPrivilegeBadge(value as string),
            },
            {
              key: 'statusCode',
              header: 'Status',
              width: '100px',
              render: (value: any) => {
                const status = getStatusDisplay(value as string);
                return <span className={`badge ${status.className}`} style={{ fontSize: '0.75rem' }}>{status.label}</span>;
              },
            },
          ]}
          data={gridData}
          filters={[
            {
              key: 'privilegeLevel',
              label: 'Privilege',
              type: 'select',
              options: [
                { value: 'user', label: 'User' },
                { value: 'admin', label: 'Admin' },
                { value: 'support', label: 'Support' },
                { value: 'super_admin', label: 'Super Admin' },
              ],
            },
          ]}
          rowKey="id"
          enableRecentlyUsed
          recentlyUsedKey="tenant-user-master-search"
          searchPlaceholder="Search by username, name, or email... (F2 to focus)"
        />
      )}
    </div>
  );
};

export default TenantUserMasterScreen;
